// // src/components/common/RequireAuth/RequireAuth.jsx
// src/components/common/RequireAuth/RequireAuth.jsx
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress, Alert } from '@mui/material';
import { clearError } from '../../../store/slices/authSlice';
import { ROUTES } from '../../../utils/constants';

const RequireAuth = ({ 
  children, 
  requiredRole = null, 
  requiredRoles = null, // Support multiple roles
  redirectTo = ROUTES.LOGIN,
  unauthorizedRedirectTo = ROUTES.DASHBOARD
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, role, loading, sessionChecked, error } = useSelector((state) => state.auth);

  // Clear any auth errors after a delay
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Helper function to check if user has required role
  const hasRequiredRole = (userRole, requiredRole, requiredRoles) => {
    if (!userRole) return false;
    
    // If specific role is required
    if (requiredRole) {
      return userRole === requiredRole;
    }
    
    // If multiple roles are allowed
    if (requiredRoles && Array.isArray(requiredRoles)) {
      return requiredRoles.includes(userRole);
    }
    
    // If no role requirement, just need to be authenticated
    return true;
  };

  // Show loading spinner while checking authentication or refreshing token
  if (loading || !sessionChecked) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress color="primary" size={40} />
        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
          Checking authentication...
        </Box>
      </Box>
    );
  }

  // Show error message if there's an auth error
  if (error && !isAuthenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        px={2}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Navigate to={redirectTo} state={{ from: location }} replace />
        </Box>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access
  const hasAccess = hasRequiredRole(role, requiredRole, requiredRoles);
  
  if (!hasAccess) {
    console.warn(`Access denied. User role: ${role}, Required: ${requiredRole || requiredRoles}`);
    return <Navigate to={unauthorizedRedirectTo} replace />;
  }

  return children;
};

// Enhanced wrapper components for specific roles
export const RequireAdmin = ({ children, ...props }) => (
  <RequireAuth requiredRole="Admin" {...props}>
    {children}
  </RequireAuth>
);

export const RequireNLAAdmin = ({ children, ...props }) => (
  <RequireAuth requiredRole="NLAADMIN" {...props}>
    {children}
  </RequireAuth>
);

export const RequireStationManager = ({ children, ...props }) => (
  <RequireAuth requiredRole="StationManager" {...props}>
    {children}
  </RequireAuth>
);

export const RequireManagerOrAdmin = ({ children, ...props }) => (
  <RequireAuth requiredRoles={['StationManager', 'Admin', 'NLAADMIN', 'SuperAdmin']} {...props}>
    {children}
  </RequireAuth>
);

export const RequireAnyRole = ({ children, ...props }) => (
  <RequireAuth {...props}>
    {children}
  </RequireAuth>
);

export default RequireAuth;



// import React from 'react';
// import { Navigate, useLocation } from 'react-router-dom';
// import { useSelector } from 'react-redux';
// import { Box, CircularProgress } from '@mui/material';
// import { ROUTES } from '../../../utils/constants';

// const RequireAuth = ({ children, requiredRole = null }) => {
//   const location = useLocation();
//   const { isAuthenticated, role, loading } = useSelector((state) => state.auth);

//   // Show loading spinner while checking authentication
//   if (loading) {
//     return (
//       <Box
//         display="flex"
//         justifyContent="center"
//         alignItems="center"
//         minHeight="100vh"
//       >
//         <CircularProgress color="primary" />
//       </Box>
//     );
//   }

//   // Redirect to login if not authenticated
//   if (!isAuthenticated) {
//     return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
//   }

//   // Check role-based access if required
//   if (requiredRole && role !== requiredRole) {
//     // You could redirect to a "Not Authorized" page instead
//     return <Navigate to={ROUTES.DASHBOARD} replace />;
//   }

//   return children;
// };

// export default RequireAuth;